import React from 'react';

import { Mark, Glyph } from './LumioMark.styles';

/** The multicolour Lumio brand mark (the same asset the header CreditsChip uses). Used wherever
 *  we label something as Lumio's, in place of a generic sparkle glyph. */
const LUMIO_MARK_SRC = '/assets/lumio/lumio-mark.svg';

export const LumioMark = ({ size = 14 }: { size?: number }): React.ReactElement => (
  <Mark src={LUMIO_MARK_SRC} alt="" aria-hidden="true" $size={size} />
);

/** The Lumio mark's exact shape (from lumio-mark.svg), rendered SOLID in `currentColor` — use this,
 *  never a generic sparkle/star, when a "lumio icon" should take the surrounding text colour (e.g.
 *  inline in a filled button). Prefer {@link LumioMark} when the brand gradient is wanted. */
export const LumioGlyph = ({ size = 14 }: { size?: number }): React.ReactElement => (
  <Glyph viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" $size={size}>
    <path d="M9.44905 1.82379C10.2548 -0.534285 13.5131 -0.605701 14.4684 1.60954L14.5493 1.82514L15.6367 5.00518C15.8859 5.73447 16.2886 6.40185 16.8177 6.96228C17.3467 7.52271 17.9898 7.96316 18.7036 8.25393L18.996 8.36308L22.176 9.44914C24.5341 10.2549 24.6055 13.5131 22.3916 14.4685L22.176 14.5493L18.996 15.6367C18.2664 15.8858 17.5988 16.2884 17.0381 16.8175C16.4774 17.3465 16.0367 17.9897 15.7458 18.7036L15.6367 18.9946L14.5506 22.176C13.7448 24.5341 10.4866 24.6055 9.53259 22.3916L9.44905 22.176L8.36298 18.996C8.11394 18.2664 7.7113 17.5988 7.18224 17.0381C6.65317 16.4775 6.01 16.0368 5.29611 15.7459L5.00506 15.6367L1.825 14.5507C-0.534441 13.7449 -0.605857 10.4867 1.6094 9.53268L1.825 9.44914L5.00506 8.36308C5.73436 8.11387 6.40173 7.71116 6.96217 7.18211C7.5226 6.65306 7.96306 6.00997 8.25383 5.29623L8.36298 5.00518L9.44905 1.82379Z" />
  </Glyph>
);

/** The Lumio mark as an OUTLINE (hollow) glyph in `currentColor` — same shape, stroked not filled, so
 *  it takes the surrounding text colour. Use inline in a filled button where the multicolour mark
 *  would not read against the fill. */
export const LumioGlyphOutline = ({ size = 14 }: { size?: number }): React.ReactElement => (
  // Padded viewBox: the mark's path reaches just past 0..24 and the stroke sits half outside it, so a
  // 0 0 24 24 box clips the star's points. The extra margin lets the full silhouette + stroke render.
  <Glyph
    viewBox="-2 -2 28 28"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.9}
    strokeLinejoin="round"
    strokeLinecap="round"
    aria-hidden="true"
    $size={size}
  >
    <path d="M9.44905 1.82379C10.2548 -0.534285 13.5131 -0.605701 14.4684 1.60954L14.5493 1.82514L15.6367 5.00518C15.8859 5.73447 16.2886 6.40185 16.8177 6.96228C17.3467 7.52271 17.9898 7.96316 18.7036 8.25393L18.996 8.36308L22.176 9.44914C24.5341 10.2549 24.6055 13.5131 22.3916 14.4685L22.176 14.5493L18.996 15.6367C18.2664 15.8858 17.5988 16.2884 17.0381 16.8175C16.4774 17.3465 16.0367 17.9897 15.7458 18.7036L15.6367 18.9946L14.5506 22.176C13.7448 24.5341 10.4866 24.6055 9.53259 22.3916L9.44905 22.176L8.36298 18.996C8.11394 18.2664 7.7113 17.5988 7.18224 17.0381C6.65317 16.4775 6.01 16.0368 5.29611 15.7459L5.00506 15.6367L1.825 14.5507C-0.534441 13.7449 -0.605857 10.4867 1.6094 9.53268L1.825 9.44914L5.00506 8.36308C5.73436 8.11387 6.40173 7.71116 6.96217 7.18211C7.5226 6.65306 7.96306 6.00997 8.25383 5.29623L8.36298 5.00518L9.44905 1.82379Z" />
  </Glyph>
);

export default LumioMark;
